import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { coursesDetail } from "../../assets/data/coursesDetail";
import { FaStar, FaRegStar } from "react-icons/fa";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import i18n from "../../i18n/i18n"; // si no lo importaste aún
import logo from "../../assets/logo.png";
import "./CourseDetailPage.css";

import level1Icon from "../../assets/courses/icons/level_1.png";
import level2Icon from "../../assets/courses/icons/level_2.png";
import level3Icon from "../../assets/courses/icons/level_3.png";

const levelIconMap = {
  Principiante: level1Icon,
  Intermedio: level2Icon,
  Avanzado: level3Icon,
};

export default function CourseDetailPage() {
  const currentLang = i18n.language.startsWith("pt") ? "pt" : "es";
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("courseDetail");
  const [showAll, setShowAll] = useState(false);

  // Scroll al cargar
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Buscar el curso por ID
  const course = coursesDetail.find((c) => c.id === id);

  // Si no existe el curso
  if (!course) {
    return (
      <div className="course-not-found-container">
        <img src={logo} alt="ZY Logo" className="course-not-found-logo" />
        <h1 className="course-not-found-title">{t("notFound.title")}</h1>
        <p className="course-not-found-message">{t("notFound.message")}</p>
        <div className="course-not-found-actions">
          <button className="course-back-btn" onClick={() => navigate(-1)}>
            {t("notFound.back")}
          </button>
          <Link to="/" className="course-home-link">
            {t("notFound.home")}
          </Link>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────
  // Traducciones con fallback seguro
  // ──────────────────────────────────────────────
  const courseT = t(`courses.${course.id}.title`, course.title);
  const descriptionT = t(
    `courses.${course.id}.description`,
    course.description
  );

  const extrasT = t(`courses.${course.id}.extras`, {
    returnObjects: true,
    defaultValue: course.extras,
  });
  const modulesT = t(`courses.${course.id}.modules`, {
    returnObjects: true,
    defaultValue: course.modules,
  });

  // Si t() devolvió un string en lugar de array, corregimos
  const safeExtras = Array.isArray(extrasT) ? extrasT : course.extras || [];
  const safeModules = Array.isArray(modulesT) ? modulesT : course.modules || [];

  const levelT = t(`levels.${course.details.level}`, course.details.level);
  const modalityT = t(
    `courses.${course.id}.details.modality`,
    course.details.modality
  );

  const levelIcon = levelIconMap[course.details.level];
  const modulesToShow = showAll ? safeModules : safeModules.slice(0, 2);

  // ──────────────────────────────────────────────
  // Render principal
  // ──────────────────────────────────────────────
  return (
    <main className="course-detail-container">
      {/* Banner del curso */}
      <section
        className="detail-banner"
        style={{
          backgroundImage: `url(${
            course.banners[currentLang] || course.banners.es
          })`,
        }}
      />

      <div className="detail-content-grid">
        {/* Contenido principal */}
        <div className="detail-main">
          <h1 className="detail-title">{courseT}</h1>

          <p className="detail-description">
            {descriptionT.split("\n").map((line, i) => (
              <span key={i}>
                {line.trim()}
                <br />
              </span>
            ))}
          </p>

          <h2>{t("sections.modules")}</h2>
          <ul className="modules-list">
            {modulesToShow.map((modTitle, idx) => (
              <li key={idx}>
                <Link
                  to={`/cursos/${course.id}/modulo-${idx + 1}`}
                  className="module-link"
                >
                  <span className="module-title">
                    <strong>Módulo {idx + 1}:</strong> {modTitle}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Mostrar/Ocultar módulos */}
          {safeModules.length > 2 && (
            <button
              className="toggle-modules"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? (
                <>
                  <FiChevronUp /> {t("sections.hide")}
                </>
              ) : (
                <>
                  <FiChevronDown /> {t("sections.seeAll")}
                </>
              )}
            </button>
          )}
        </div>

        {/* Sidebar de detalles */}
        <aside className="detail-sidebar">
          <div className="detail-card">
            <h3>{t("sections.details")}</h3>

            <div className="detail-row">
              <span>{t("sections.rating")}</span>
              <div className="rating">
                {Array.from({ length: 5 }, (_, i) =>
                  i < Math.round(course.details.rating) ? (
                    <FaStar key={i} />
                  ) : (
                    <FaRegStar key={i} />
                  )
                )}
                <span> ({course.details.rating.toFixed(1)})</span>
              </div>
            </div>

            <div className="detail-row">
              <span>{t("sections.level")}</span>
              <div className="level-wrapper">
                <img src={levelIcon} alt={levelT} className="meta-icon-img" />
                <span>{levelT}</span>
              </div>
            </div>

            <div className="detail-row">
              <span>{t("sections.modality")}</span>
              <span>{modalityT}</span>
            </div>
          </div>

          <div className="detail-card">
            <h3>{t("sections.youWillLearn")}</h3>
            <ul className="extras-list">
              {safeExtras.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      {/* Botón inferior para volver */}
      <button className="back-btn" onClick={() => navigate("/cursos")}>
        {t("sections.backButton")}
      </button>
    </main>
  );
}
